import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Package, Truck } from "lucide-react";

export default function PackagingDashboard() {
  const { user, loading } = useAuth();
  const [queue, setQueue] = useState({ bespoke: [], orders: [] });
  const [past, setPast] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ items_ok: true, color_match: true, weight_before_g: "", weight_after_g: "", courier: "Shiprocket Express", shipment_address: "", notes: "" });

  const refresh = async () => {
    try {
      const [q, p] = await Promise.all([api.get("/packaging/queue"), api.get("/packaging")]);
      setQueue(q.data); setPast(p.data);
    } catch {}
  };
  useEffect(() => { if (user && ["super_admin", "major_admin", "admin", "packaging_manager"].includes(user.role)) refresh(); }, [user]);

  if (loading) return <div className="sq-container py-20 sq-eyebrow">Loading despatch…</div>;
  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in</Link></div>;
  if (!["super_admin", "major_admin", "admin", "packaging_manager"].includes(user.role)) return <div className="sq-container py-20">Access denied.</div>;

  const submit = async () => {
    if (!target) return toast.error("Select an order");
    try {
      await api.post("/packaging", { ...form, order_id: target.id, weight_before_g: Number(form.weight_before_g), weight_after_g: Number(form.weight_after_g) });
      toast.success(`Packaged ${target.id} & dispatched`);
      setTarget(null);
      setForm({ ...form, weight_before_g: "", weight_after_g: "", shipment_address: "", notes: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <main className="sq-container py-10" data-testid="packaging-dashboard">
      <div className="flex items-end justify-between border-b border-black/10 pb-8">
        <div>
          <div className="sq-eyebrow text-squire-jeans">Atelier Despatch</div>
          <h1 className="font-display text-5xl mt-2">Packaging</h1>
        </div>
        <div className="sq-eyebrow">{user.role.replace("_", " ")} · {user.id}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        <section className="lg:col-span-2">
          <div className="sq-eyebrow mb-3">Queue · {queue.bespoke.length + queue.orders.length}</div>
          <div className="space-y-2 max-h-[640px] overflow-y-auto sq-scroll">
            {[...queue.bespoke.map((b) => ({ ...b, _type: "bespoke" })), ...queue.orders.map((o) => ({ ...o, _type: "order" }))].map((o) => (
              <button key={o.id} data-testid={`pack-pick-${o.id}`} onClick={() => { setTarget(o); setForm({ ...form, shipment_address: `${o.address?.line1 || ""} ${o.address?.city || ""} ${o.address?.pincode || ""}` }); }} className={`block w-full text-left border p-4 ${target?.id === o.id ? "border-squire-jeans" : "border-black/10 hover:border-black"}`}>
                <div className="flex justify-between"><div className="font-display">{o.id} · {o._type === "bespoke" ? o.garment_type : `${o.items?.length || 0} items`}</div><div className="font-semibold">{formatINR(o.total)}</div></div>
                <div className="sq-eyebrow text-[10px] mt-1">{o.user_id} · {o.stage || o.status} · {o.address?.city}</div>
              </button>
            ))}
            {queue.bespoke.length + queue.orders.length === 0 && <div className="text-sm text-[#4B5563]">Nothing in queue.</div>}
          </div>
        </section>

        <section>
          <div className="sq-eyebrow mb-3 flex items-center gap-2"><Package size={12} /> Pack & Dispatch</div>
          <div className="border border-black/10 p-5">
            {target ? <div className="font-display text-lg mb-3">{target.id}</div> : <div className="text-sm text-[#4B5563]">Pick an order →</div>}
            <label className="flex items-center gap-2 mt-2 text-sm"><input data-testid="pack-items-ok" type="checkbox" checked={form.items_ok} onChange={(e) => setForm({ ...form, items_ok: e.target.checked })} /> Items match order</label>
            <label className="flex items-center gap-2 mt-2 text-sm"><input data-testid="pack-color-ok" type="checkbox" checked={form.color_match} onChange={(e) => setForm({ ...form, color_match: e.target.checked })} /> Colours match spec</label>
            {[["weight_before_g", "Weight before (g)"], ["weight_after_g", "Weight after (g)"], ["shipment_address", "Shipment address"], ["courier", "Courier"], ["notes", "Notes"]].map(([k, l]) => (
              <label key={k} className="block mt-3">
                <span className="sq-eyebrow text-[10px]">{l}</span>
                <input data-testid={`pack-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
              </label>
            ))}
            <button data-testid="pack-submit" onClick={submit} disabled={!target} className="sq-btn-primary w-full mt-5 disabled:opacity-30"><Truck size={14} /> Dispatch</button>
          </div>

          <div className="sq-eyebrow mt-8 mb-3">Recent · {past.length}</div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto sq-scroll">
            {past.map((p) => (
              <div key={p.id} className="border border-black/10 p-3 text-xs">
                <div className="font-display">{p.id}</div>
                <div className="sq-eyebrow text-[10px]">{p.order_id} · {p.shiprocket_id}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
